import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import { Reveal, Stagger, StaggerItem, MaskedTitle, MagneticButton, EASE } from "@/components/motion";
import { ArticlePage } from "@/pages/ArticlePage";
import { ARTICLE_BY_SLUG } from "@/data/articles";
import { PRIVACY_POLICY, TERMS_CONDITIONS } from "@/data/legal";
import { useGuidebookGate, GuidebookGateModal } from "@/components/GuidebookGate";
import AdminLeadsPage from "@/pages/AdminLeads";
import "@/styles/site.css";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/k6fqt6b0_logo-transparent.png";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/kqnf668o_logo-white-transparent.png";

// Per-route SEO. Titles ≤ 60 chars; descriptions ≤ 160 chars; primary keyword up front.
const SEO_META = {
  "/":               { title: "Players Health — Youth Sports Insurance & Athlete Safety", description: "Sports insurance for youth, fitness, K-12 and NIL programs. Coverage, risk visibility and safety tools built for sport. Get a quote today.", crumb: "Home" },
  "/insurance":      { title: "Sports Insurance Coverage | GL, Accident, D&O & More", description: "Modular sports insurance: General Liability, Participant Accident, D&O, Equipment, Crime, Cyber. Coverage built for how your program actually runs.", crumb: "Insurance" },
  "/youth-sports":   { title: "Youth Sports Insurance for Clubs, Leagues & Camps", description: "Youth sports insurance built around volunteers, participants, and travel. Sport-specific coverage, safety insight and pricing built for youth programs.", crumb: "Youth & Amateur" },
  "/fitness":        { title: "Fitness Studio & Gym Insurance | Group, Strength, Yoga", description: "Fitness business insurance for group fitness, strength, CrossFit, yoga and pilates studios. Coverage that scales with your business.", crumb: "Fitness" },
  "/collegiate":     { title: "Collegiate & NIL Insurance | Critical Injury, Disgrace", description: "NIL-era insurance for collegiate athletes and departments. Critical Injury, Performance Bonus, Transfer, Disgrace and Downside Protection.", crumb: "Collegiate & NIL" },
  "/schools":        { title: "K-12 Athletics Insurance | Student Accident & Catastrophic", description: "K-12 sports insurance for districts and interscholastic programs: student accident, catastrophic, concussion care, and special risk coverage.", crumb: "K-12 Schools" },
  "/brokers":        { title: "Sports Insurance for Brokers | Get Appointed with Players Health", description: "A smarter way for brokers to place sports coverage. Broad appetite across youth, fitness and NIL. Instant quotes, digital bind, commission portal.", crumb: "Brokers" },
  "/athlete-safety": { title: "Athlete Safety Programs, Score & Risk Management", description: "Athlete and member safety built into your program. Background checks, SafeSport training, incident reporting and the Players Health Safety Score.", crumb: "Athlete Safety" },
  "/resources":      { title: "Sports Safety Resources, Guidebooks & Research", description: "Guidebooks, research and articles on athlete safety, concussion care, abuse prevention, camp safety and risk management for sports programs.", crumb: "Resources" },
  "/guidebooks":     { title: "Sports Safety Guidebooks | Free Downloads for Programs", description: "Free sports safety guidebooks: concussion, abuse prevention, camp safety, background checks, active shooter and fitness risk management.", crumb: "Guidebooks" },
  "/about":          { title: "About Players Health | Player-First Sports Insurance", description: "Players Health builds insurance and safety tools that put the athlete first. Learn about our mission, team and top-rated carrier partners.", crumb: "About Us" },
  "/support":        { title: "Contact Players Health | Talk to a Sports Specialist", description: "Talk to a Players Health sports specialist about coverage, claims or safety programs. Get a callback within one business day.", crumb: "Contact Us" },
  "/quote":          { title: "Get a Sports Insurance Quote | Quote in Minutes", description: "Get a tailored sports insurance quote in minutes. Tell us about your program and a Players Health specialist will follow up same-day.", crumb: "Get a Quote" },
  "/privacy":        { title: "Privacy Policy | Players Health", description: "Read the Players Health privacy policy: how we collect, use, share and protect your personal information across our sports insurance platform.", crumb: "Privacy Policy" },
  "/terms":          { title: "Terms & Conditions | Players Health", description: "Terms & Conditions for using playershealth.com and Players Health services. Please read carefully before using the site.", crumb: "Terms" },
};
const QUOTE_URL = "https://phquote.com/quote/apply?utm_source=website&utm_medium=referral&utm_campaign=homepage";

const ATHLETE_IMG = {
  basketball: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/iujq4ymb_photo-basketball.png",
  soccer:     "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/un71o1ul_photo-soccer.jpg",
  tennis:     "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/57xhxpsr_photo-tennis-teal.jpg",
  fitness:    "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ahxud74u_photo-tennis-topview.png",
  youth:      "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/un71o1ul_photo-soccer.jpg",
  field:      "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/iujq4ymb_photo-basketball.png",
  k12:        "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/3jnh5otf_PH_PhotoYouth%2010%20%281%29.jpg",
  safety:     "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/o1t99uny_photo-tennis-topview.jpg",
  brokers:    "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/5ne0lmkx_PH_PhotoPro%2008.jpg",
  youthSports:"https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/5ajjkrkk_PH_PhotoYouth%2008%20%281%29.jpg",
  fitnessImg: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/srs5x93v_PH_PhotoFitness%2001.jpg",
  collegiate: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/2tca0ebg_PH_PhotoCollege%2011.jpg",
  resources:  "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/lfh5eaq5_photo-tennis-topview.jpg",
};

// Old playershealth.com URLs → new routes (client-side safety net alongside Cloudflare 301s).
const OLD_PATH_MAP = {
  "/about-us": "/about", "/careers": "/about", "/foundation": "/about",
  "/build-a-quote": "/quote",
  "/contact": "/support", "/submit-a-ticket": "/support",
  "/courses": "/resources", "/healthandsafetysummit": "/resources",
  "/resource-categories/athlete-safety-resources": "/resources", "/resource-categories/featured-insights": "/resources",
  "/resource-categories/in-the-news": "/resources", "/resource-categories/upcoming-events": "/resources",
  "/resources/players-health-guidebooks": "/guidebooks",
  "/our-solutions": "/insurance", "/solutions/insurance": "/insurance",
  "/player-safety": "/athlete-safety", "/safety-hub": "/athlete-safety", "/solutions/athlete-safety": "/athlete-safety",
  "/terms-conditions": "/terms",
  "/get-the-coverage-usa-football-trusts": "/youth-sports", "/youth-sports-insurance": "/youth-sports",
  "/nilprotection": "/collegiate", "/nilcontractprotection": "/collegiate", "/nilsummitexchange": "/collegiate", "/nilsummitexchange-sponsor": "/collegiate",
  "/who-we-serve/youth-sports": "/youth-sports", "/who-we-serve/fitness": "/fitness", "/who-we-serve/collegiate": "/collegiate",
  "/who-we-serve/k-12-schools": "/schools", "/who-we-serve/brokers-and-agents": "/brokers",
};
const resolveLegacy = (p) => {
  if (OLD_PATH_MAP[p]) return OLD_PATH_MAP[p];
  if (p.startsWith("/the-safest-way-to-play")) return "/youth-sports";
  if (p.startsWith("/resources/") && !ARTICLE_BY_SLUG[p.slice("/resources/".length)]) return "/resources";
  return null;
};
const getPath = () => {
  const raw = window.location.pathname || "/";
  const p = raw.length > 1 ? raw.replace(/\/+$/, "").toLowerCase() : "/";
  const target = resolveLegacy(p) || (p !== raw ? p : null);
  if (target) window.history.replaceState({}, "", target + window.location.search + window.location.hash);
  return target || p;
};

const Logo = ({ light }) => (
  <a href="/" className="nav-logo" data-testid="nav-logo">
    <img src={light ? LOGO_WHITE : LOGO} alt="Players Health" style={{ height: 32, width: "auto" }} />
  </a>
);

const NavIcon = ({ name }) => {
  const s = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "youth":
      return (
        <svg {...s}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 20c.4-3.1 2.7-5 5.5-5s5.1 1.9 5.5 5" />
          <circle cx="17" cy="9" r="2.4" />
          <path d="M14 20c.3-2.4 2-3.8 3.9-3.8s3.5 1.2 3.9 3.4" />
        </svg>
      );
    case "fitness":
      return (
        <svg {...s}>
          <path d="M4 9v6M7 6.5v11M10 8.5v7h4v-7M17 6.5v11M20 9v6" />
        </svg>
      );
    case "collegiate":
      return (
        <svg {...s}>
          <path d="M8 4h8v4a4 4 0 0 1-8 0V4z" />
          <path d="M8 6H5v2a3 3 0 0 0 3 3M16 6h3v2a3 3 0 0 1-3 3" />
          <path d="M10 12v3h4v-3M9 20h6M12 15v5" />
        </svg>
      );
    case "schools":
      return (
        <svg {...s}>
          <path d="M4 20V10l8-5 8 5v10" />
          <path d="M9 20v-6h6v6M12 5v2" />
          <path d="M4 20h16" />
        </svg>
      );
    case "brokers":
      return (
        <svg {...s}>
          <rect x="3" y="7" width="18" height="12" rx="1.5" />
          <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 12h18" />
        </svg>
      );
    default:
      return null;
  }
};

const INSURANCE_SUBNAV = [
  { label: "Youth & Amateur", to: "/youth-sports", icon: "youth", blurb: "Leagues, clubs, tournaments" },
  { label: "Fitness", to: "/fitness", icon: "fitness", blurb: "Gyms, studios, instructors" },
  { label: "Collegiate", to: "/collegiate", icon: "collegiate", blurb: "NCAA, NAIA, NJCAA, club" },
  { label: "K-12 Schools", to: "/schools", icon: "schools", blurb: "Districts & athletic depts" },
  { label: "Brokers", to: "/brokers", icon: "brokers", blurb: "Appointed broker network" },
];

const InsuranceDropdown = ({ route }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const active = route === "/insurance" || INSURANCE_SUBNAV.some(s => s.to === route);
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 260);
  };
  const cancelClose = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  return (
    <div
      className={`nav-item-wrap ${open ? "open" : ""}`}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      data-testid="nav-insurance-wrap"
    >
      <a
        href="/insurance"
        className={`nav-link nav-link-trigger ${active ? "active" : ""}`}
        data-testid="nav-link-insurance"
        onClick={() => setOpen(false)}
      >
        Insurance
        <span className="caret" aria-hidden="true">
          <svg viewBox="0 0 10 10" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.6">
            <polyline points="2,3.5 5,6.5 8,3.5" />
          </svg>
        </span>
      </a>
      <div
        className={`insurance-mega ${open ? "open" : ""}`}
        data-testid="nav-insurance-mega"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        {INSURANCE_SUBNAV.map(s => (
          <a
            key={s.to}
            href={`${s.to}`}
            className={`insurance-mega-item ${route === s.to ? "active" : ""}`}
            data-testid={`nav-insurance-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setOpen(false)}
          >
            <span className="mega-icon-badge" aria-hidden="true"><NavIcon name={s.icon} /></span>
            <span className="mega-copy">
              <span className="mega-label">{s.label}</span>
              <span className="mega-blurb">{s.blurb}</span>
            </span>
            <span className="arrow">→</span>
          </a>
        ))}
      </div>
    </div>
  );
};

const NAV_LINKS = [
  { label: "Resources", to: "/resources" },
  { label: "Athlete Safety", to: "/athlete-safety" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/support" },
];

const Nav = ({ route }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [route]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  const mobileLinks = [
    { label: "Insurance", to: "/insurance" },
    ...INSURANCE_SUBNAV.map(s => ({ label: `  · ${s.label}`, to: s.to })),
    ...NAV_LINKS,
    { label: "Resources", to: "/resources" },
  ];
  return (
    <>
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="container nav-inner">
          <Logo light />
          <div className="nav-links">
            <InsuranceDropdown route={route} />
            {NAV_LINKS.map(n => (
              <a
                key={n.to}
                href={`${n.to}`}
                className={`nav-link ${route === n.to ? "active" : ""}`}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
          </div>
          <div className="nav-cta">
            <MagneticButton href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-accent" data-testid="nav-get-quote">Get a Quote</MagneticButton>
            <button
              type="button"
              className="nav-burger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              data-testid="nav-burger"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          </div>
        </div>
      </nav>
      <div className={`menu-scrim ${menuOpen ? "open" : ""}`} onClick={() => setMenuOpen(false)} />
      <aside className={`menu-panel ${menuOpen ? "open" : ""}`} aria-hidden={!menuOpen} data-testid="mobile-menu">
        <div className="menu-head">
          <Logo light />
          <button type="button" className="menu-close" aria-label="Close menu" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <div className="menu-body">
          <div>
            <div className="menu-section-label">Menu</div>
            <div className="menu-list">
              {mobileLinks.map((n, i) => (
                <a
                  key={`${n.to}-${i}`}
                  href={`${n.to}`}
                  className="menu-item"
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-menu-${n.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span>{n.label}</span>
                  <span className="arrow">→</span>
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="menu-foot">
          <MagneticButton href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="mobile-menu-cta">Get a Quote</MagneticButton>
        </div>
      </aside>
    </>
  );
};

const Hero = ({ eyebrow, title, body, img, primaryCta = "Get a Quote", primaryHref = QUOTE_URL, secondaryCta = "Talk to us", secondaryHref = "/support" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-22, 34]);
  const primaryExternal = primaryHref.startsWith("http");
  return (
    <section className="hero-dark" ref={ref}>
      <div className="hero-motif" />
      <div className="hero-glow" aria-hidden="true" />
      <div className="container">
        <div className="hero-grid">
          <div className="hero-copy">
            <motion.span className="eyebrow on-dark" data-testid="hero-eyebrow"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>{eyebrow}</motion.span>
            <MaskedTitle text={title} className="display display-xl" />
            <motion.p className="lede on-dark" data-testid="hero-body"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.38 }}>{body}</motion.p>
            <motion.div className="hero-actions"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.5 }}>
              <MagneticButton
                href={primaryHref}
                target={primaryExternal ? "_blank" : undefined}
                rel={primaryExternal ? "noopener noreferrer" : undefined}
                className="btn btn-primary"
                data-testid="hero-cta-primary"
              >{primaryCta}</MagneticButton>
              <a href={secondaryHref} className="btn btn-ghost" data-testid="hero-cta-secondary">{secondaryCta}</a>
            </motion.div>
          </div>
          <motion.div className="hero-photo"
            initial={{ opacity: 0, clipPath: "inset(14% 14% 14% 14% round 4px)" }}
            animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0% round 4px)" }}
            transition={{ duration: 1.05, ease: EASE, delay: 0.15 }}>
            <motion.img src={img} alt="" width="1200" height="1500" style={{ y, scale: 1.14 }} fetchPriority="high" decoding="async" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FooterCTA = () => (
  <section className="footer-cta">
    <Reveal className="container footer-cta-inner">
      <div>
        <h2 className="display display-m" style={{ color: "#fff", margin: 0 }}>Ready to protect your program?</h2>
        <p className="lede on-dark mt-6">Get a quote in minutes. Built for sports — backed by people who get it.</p>
      </div>
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <MagneticButton href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="footer-cta-quote">Get a Quote</MagneticButton>
        <a href="/support" className="btn btn-ghost" data-testid="footer-cta-talk">Talk to us</a>
      </div>
    </Reveal>
  </section>
);

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-grid">
        <div>
          <Logo light />
          <p className="mt-6" style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 280 }}>The safety platform for youth, amateur, and small-business sports.</p>
        </div>
        {[
          ["Insurance", [["/insurance","Overview"],["/fitness","Fitness"],["/collegiate","Collegiate"],["/schools","K-12 Schools"],["/brokers","Brokers"]]],
          ["Safety", [["/athlete-safety","Athlete Safety"],["/youth-sports","Youth Sports"],["/resources","Resources"]]],
          ["Company", [["/about","About"],["/about","Careers"],["/support","Support"],["https://playershealthfoundation.com/","Foundation"]]],
          ["Get Started", [[QUOTE_URL,"Get a Quote"],["/support","Contact Sales"]]],
        ].map(([h, items]) => (
          <div key={h}>
            <h5>{h}</h5>
            <ul>{items.map(([to, label]) => {
              const ext = typeof to === "string" && to.startsWith("http");
              return (
                <li key={to+label}>
                  <a href={ext ? to : `${to}`} target={ext ? "_blank" : undefined} rel={ext ? "noopener noreferrer" : undefined}>{label}</a>
                </li>
              );
            })}</ul>
          </div>
        ))}
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Players Health. All rights reserved.</span>
        <div className="footer-legal">
          <a href="/privacy" data-testid="footer-privacy">Privacy Policy</a>
          <span aria-hidden="true">·</span>
          <a href="/terms" data-testid="footer-terms">Terms &amp; Conditions</a>
          <span aria-hidden="true">·</span>
          <button type="button" className="footer-legal-btn" onClick={() => window.dispatchEvent(new Event("ph:open-cookie-prefs"))} data-testid="footer-cookie-prefs">Cookie Preferences</button>
        </div>
      </div>
    </div>
  </footer>
);

const LegalPage = ({ doc }) => (
  <>
    <section className="legal-hero">
      <div className="container">
        <span className="eyebrow" data-testid="legal-eyebrow">{doc.eyebrow}</span>
        <h1 className="display display-xl mt-6" data-testid="legal-title">{doc.title}</h1>
        <p className="legal-updated mt-6">{doc.updated}</p>
      </div>
    </section>
    <section className="section legal-body">
      <div className="container legal-container">
        {doc.intro && doc.intro.split("\n\n").map((p, i) => <p key={i} className="legal-p">{p}</p>)}
        {doc.sections.map((s, i) => (
          <div key={i} className="legal-section">
            <h2 className="legal-h2">{s.h}</h2>
            {s.p && s.p.split("\n\n").map((p, j) => <p key={j} className="legal-p">{p}</p>)}
            {s.list && (
              <ul className="legal-list">
                {s.list.map(([h, p]) => (
                  <li key={h}><strong>{h}:</strong> {p}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
    <FooterCTA />
  </>
);

const COOKIE_KEY = "ph_cookie_prefs_v1";
const CookieBanner = () => {
  const [prefs, setPrefs] = useState(() => {
    try { const raw = localStorage.getItem(COOKIE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({ essentials: true, analytics: true, marketing: true, personalization: true });

  useEffect(() => {
    const openHandler = () => { setDraft(prefs || { essentials: true, analytics: true, marketing: true, personalization: true }); setModalOpen(true); };
    window.addEventListener("ph:open-cookie-prefs", openHandler);
    return () => window.removeEventListener("ph:open-cookie-prefs", openHandler);
  }, [prefs]);

  const save = (next) => {
    const record = { ...next, timestamp: new Date().toISOString() };
    try { localStorage.setItem(COOKIE_KEY, JSON.stringify(record)); } catch {}
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: next.analytics ? "granted" : "denied",
        ad_storage: next.marketing ? "granted" : "denied",
        ad_user_data: next.marketing ? "granted" : "denied",
        ad_personalization: next.personalization ? "granted" : "denied",
      });
    }
    setPrefs(record);
    setModalOpen(false);
  };
  const acceptAll = () => save({ essentials: true, analytics: true, marketing: true, personalization: true });
  const rejectAll = () => save({ essentials: true, analytics: false, marketing: false, personalization: false });

  const showBar = !prefs && !modalOpen;

  return (
    <>
      {showBar && (
        <div className="cookie-bar" role="dialog" aria-label="Cookie notice" data-testid="cookie-bar">
          <div className="container cookie-bar-inner">
            <p className="cookie-bar-copy">
              We use cookies to run the site, measure usage, and improve your experience. See our <a href="/privacy">Privacy Policy</a>.
            </p>
            <div className="cookie-bar-actions">
              <button type="button" className="btn btn-ghost" onClick={() => { setDraft({ essentials: true, analytics: true, marketing: true, personalization: true }); setModalOpen(true); }} data-testid="cookie-bar-manage">Manage</button>
              <button type="button" className="btn btn-ghost" onClick={rejectAll} data-testid="cookie-bar-reject">Reject All</button>
              <button type="button" className="btn btn-primary" onClick={acceptAll} data-testid="cookie-bar-accept">Accept All</button>
            </div>
          </div>
        </div>
      )}
      {modalOpen && (
        <div className="cookie-scrim" onClick={() => setModalOpen(false)} data-testid="cookie-modal">
          <div className="cookie-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="cookie-modal-head">
              <h3 className="cookie-modal-title">Manage Consent Preferences</h3>
              <button type="button" className="cookie-modal-close" aria-label="Close" onClick={() => setModalOpen(false)}>×</button>
            </div>
            <div className="cookie-modal-body">
              {[
                { key: "essentials", label: "Essentials", desc: "Necessary for the site to function. Always On.", locked: true },
                { key: "analytics", label: "Analytics", desc: "Measures usage and improves your experience." },
                { key: "marketing", label: "Marketing", desc: "Used for targeted advertising." },
                { key: "personalization", label: "Personalization", desc: "Remembers your preferences and provides enhanced features." },
              ].map(row => (
                <div key={row.key} className="cookie-row">
                  <div className="cookie-row-copy">
                    <h4 className="cookie-row-label">{row.label}</h4>
                    <p className="cookie-row-desc">{row.desc}</p>
                  </div>
                  {row.locked ? (
                    <span className="cookie-locked" data-testid={`cookie-${row.key}-locked`}>Always active</span>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={draft[row.key]}
                      className={`cookie-toggle ${draft[row.key] ? "on" : ""}`}
                      onClick={() => setDraft(d => ({ ...d, [row.key]: !d[row.key] }))}
                      data-testid={`cookie-toggle-${row.key}`}
                    >
                      <span className="cookie-toggle-thumb" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="cookie-modal-foot">
              <button type="button" className="btn btn-ghost" onClick={rejectAll} data-testid="cookie-modal-reject">Reject All</button>
              <button type="button" className="btn btn-primary" onClick={acceptAll} data-testid="cookie-modal-accept">Accept All</button>
              <button type="button" className="btn btn-dark" onClick={() => save(draft)} data-testid="cookie-modal-save">Save Preferences</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Section = ({ children, dark, alt, tight, id, noReveal }) => (
  <section id={id} className={`${tight ? "section-tight" : "section"} ${dark ? "section-dark" : ""} ${alt ? "section-alt" : ""}`}>
    {noReveal ? <div className="container">{children}</div> : <Reveal className="container">{children}</Reveal>}
  </section>
);

const TICKER = [
  { name: "USA Football", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/hexw1zw0_usa_football_black.png" },
  { name: "NFL Flag", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/m061wv78_nfl_flag_black.png" },
  { name: "Skyhawks Sports", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/dihk5g2f_skyhawks_black.webp" },
  { name: "USA Boxing", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/sljdwiyj_usa_boxing_black.png" },
  { name: "US Figure Skating", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ua1wij15_us_figure_skating_black.png" },
  { name: "Orangetheory Fitness", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ee41pr79_orangetheory_black.png" },
  { name: "Gold's Gym", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/i7cylctz_golds_gym_black.png" },
  { name: "RCX Sports", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/1i512ubp_rcx_sports_black.png" },
  { name: "Youth Athletes United", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/5webbz1p_youth_athletes_united_black.png" },
  { name: "Under the Lights Flag", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/3hhkqkzm_under_the_lights_flag_black.png" },
  { name: "Ohio Soccer Association", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/4yt83qnt_ohio_soccer_association_black.png" },
  { name: "Florida Soccer Association", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/2bqtcru2_florida_soccer_association_black.png" },
  { name: "Tippi Toes", src: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/gpr5ik1u_tippi_toes_black.png" },
];

const HomePage = () => (
  <>
    <Hero
      eyebrow="Players Health"
      title="Coverage that goes further."
      body="Insurance built for sports, visibility into your risk, and the tools to reduce it — all in one place."
      img={ATHLETE_IMG.basketball}
    />
    <Section tight>
      <div className="ticker ticker-light">
        <div className="ticker-track">
          {[...TICKER, ...TICKER].map((t, i) => (
            <div key={i} className="ticker-item ticker-item-logo" title={t.name}>
              <img src={t.src} alt={t.name} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </Section>
    <Section dark tight>
      <div className="stats">
        {[["10M+","Athletes covered"],["40,000+","Organizations protected"],["$0","To get started"],["A+","Carrier rated"]].map(([v,l]) => (
          <div key={l} className="stat"><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
    </Section>

    <Section noReveal>
      <Reveal className="sec-head">
        <div>
          <span className="eyebrow">How We Protect Your Program</span>
          <h2 className="display display-l mt-6">One platform. Total coverage.</h2>
        </div>
        <p className="lede">Insurance is the foundation. Visibility shows you where you're exposed. Prevention tools drive your claims down and protect your people.</p>
      </Reveal>
      <Stagger className="split-3">
        {[
          ["01", "Get Insured", "General liability, accident and health, D&O, equipment, and more, built for leagues, clubs, studios, gyms, and schools. Accident and health coverage matters in youth and amateur sports, where injured athletes and their families expect medical costs to be handled. This is where every program should start."],
          ["02", "See Where You're Exposed", "Your Safety Score and your own claims history show what's protected, what's exposed, and where the safety work isn't getting done yet. Your claims tell you where members and athletes are getting hurt. You see the gaps before someone else does."],
          ["03", "Reduce Your Risk", "We analyze where risk actually lives in your program, across your facilities, your staff, your activities, and your claims history. Then we give you the standards, training, and prevention tools to close those gaps. Every injury prevented is a member or athlete who stayed healthy, and a program that gets rewarded for doing the work."],
        ].map(([num, h, p]) => (
          <StaggerItem key={h} className="feature-card feature-card-numbered" data-testid={`feature-${h.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")}`}>
            <div className="card-num">{num}</div>
            <h3>{h}</h3>
            <p>{p}</p>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="mission-line" data-testid="home-mission-line">
        <p>We exist to make sport and fitness safer. Better coverage, clearer visibility, and real prevention are how we get there.</p>
      </Reveal>
    </Section>

    <Section dark noReveal>
      <Reveal className="sec-head">
        <div>
          <span className="eyebrow on-dark">Who We Serve</span>
          <h2 className="display display-l mt-6" style={{ color: "#fff" }}>Built for every level of play.</h2>
        </div>
      </Reveal>
      <Stagger className="split-3" gap={0.08}>
        {[
          { to: "/youth-sports", eb: "Youth Sports", h: "Leagues & clubs", p: "Coverage for youth leagues, travel teams, and recreation clubs.", img: ATHLETE_IMG.youthSports },
          { to: "/fitness", eb: "Fitness", h: "Gyms & studios", p: "Train hard. Insure smart. Coverage for franchises, gyms, and boxes.", img: ATHLETE_IMG.fitnessImg },
          { to: "/collegiate", eb: "Collegiate", h: "NIL-era programs", p: "Protect your athletic department in the new era of college sports.", img: ATHLETE_IMG.collegiate },
          { to: "/schools", eb: "K-12 Schools", h: "Districts & schools", p: "More than gear and game time — coverage that fits your administrators.", img: ATHLETE_IMG.k12 },
          { to: "/brokers", eb: "Brokers", h: "Agents & wholesalers", p: "A smarter way to place coverage for your sports clients.", img: ATHLETE_IMG.brokers },
        ].map(t => (
          <StaggerItem as="a" key={t.to} href={`${t.to}`} className="aud-tile" data-testid={`audience-${t.to.slice(1)}`}>
              <img src={t.img} alt="" loading="lazy" decoding="async" />
            <div className="aud-tile-content">
              <span className="aud-tile-eyebrow">{t.eb}</span>
              <div>
                <h3>{t.h}</h3>
                <p>{t.p}</p>
                <span className="aud-tile-cta">Learn more →</span>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>

    <Section alt noReveal>
      <Reveal className="sec-head">
        <div>
          <span className="eyebrow">Safety</span>
          <h2 className="display display-l mt-6">Safety you can see. Risk you can manage.</h2>
        </div>
        <p className="lede">We look at what you already have in place and what your claims history says — then give you the visibility and tools to manage and reduce risk before it becomes a claim.</p>
      </Reveal>
      <Stagger className="split-4" data-testid="home-safety-grid">
        {[
          ["01", "Safety Assessment", "A clear look at the safeguards you already have in place, and where the gaps are."],
          ["02", "Claims History", "Your claims data, decoded — so you know exactly where risk has shown up before."],
          ["03", "Risk Visibility", "One view of your program's safety posture, built from what we see across the field."],
          ["04", "Risk Management Tools", "The tools to act on what we find, so risk doesn't turn into a claim."],
        ].map(([num, h, p]) => (
          <StaggerItem key={h} className="feature-card feature-card-numbered" data-testid={`home-safety-${num}`}>
            <div className="card-num">{num}</div>
            <h3>{h}</h3>
            <p>{p}</p>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="compliance-footnote">
        <p><a href="/athlete-safety" data-testid="home-safety-cta">Learn how we help you manage risk →</a></p>
      </Reveal>
    </Section>


    <Section>
      <div className="sec-head">
        <div>
          <span className="eyebrow">Products</span>
          <h2 className="display display-l mt-6">A line-up that scales with you.</h2>
        </div>
      </div>
      {[
        ["General Liability", "Protect your organization from third-party bodily injury and property damage claims — the foundation of every sports policy."],
        ["Accident Insurance", "Excess and primary accident medical coverage for participants — fast claims, fewer headaches."],
        ["Directors & Officers", "Defend your board and staff from leadership-related claims that can sink a program."],
        ["Equipment & Property", "Cover gear, uniforms, and facility property — on-site, in transit, or at tournaments."],
        ["Crime & Cyber", "Protect funds, donor data, and registration platforms from theft and breach."],
      ].map(([h,p], i) => (
        <div key={h} className="product-row" data-testid={`product-${i}`}>
          <div className="num">0{i+1}</div>
          <div><h3>{h}</h3><p>{p}</p></div>
          <div className="arrow">→</div>
        </div>
      ))}
    </Section>

    <Section dark>
      <div className="promise-block" data-testid="our-promise">
        <span className="eyebrow on-dark">Our Promise</span>
        <p className="promise-copy mt-6">
          Every policy comes with a plan — not just a certificate. We don't just insure your program; we help you see your risk and reduce it, so fewer athletes get hurt in the first place.
        </p>
      </div>
    </Section>
    <FooterCTA />
  </>
);

const ICONS = {
  // Insurance / Lines of Coverage
  "General Liability": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/pmi1a81b_general_liability.png",
  "Accident": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/uzfe445d_accident.png",
  "D&O": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/dgx355x1_d_and_o.png",
  "Equipment": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/sy39b511_equipment.png",
  "Crime": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/buoojom1_crime.png",
  "Cyber": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/gpm1helh_cyber.png",
  // Youth Sports
  "Sport-Specific": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/n2w7odqu_sport_specific.png",
  "Safety Insight": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/12maqrmk_safety_insight.png",
  "Affordable": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/rddg08oa_affordable.png",
  // Fitness
  "Group Fitness": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ozbzxs0f_group_fitness.png",
  "Strength & CrossFit": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/cnis77ei_strength_crossfit.png",
  "Yoga & Pilates": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/sa93q8md_yoga_pilates.png",
  // K-12 Schools
  "District-Wide": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/9hgm3qu8_district_wide.png",
  "Catastrophic": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/cnx5k0sb_catastrophic.png",
  "Concussion Care": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/wbg02w3h_concussion_care.png",
  // NIL / Collegiate
  "NIL Liability": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ezymrr8v_nil_liability.png",
  "Athletic Dept.": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/x9gjfe42_athletic_dept.png",
  "Event Liability": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/yhm0df5x_event_liability.png",
  // Brokers
  "Instant Quotes": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/w9u64y6j_instant_quotes.png",
  "Digital Bind": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/g9mg91j9_digital_bind.png",
  "Commission Portal": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/br44popw_commission_portal.png",
  // About Us
  "Player-First": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/4sdyiuvr_player_first.png",
  "Tech + Touch": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/lbfh31rf_tech_touch.png",
  "Carrier-Backed": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/194p25na_carrier_backed.png",
  // Collegiate — NIL Solutions
  "Critical Injury Insurance": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/2jodpill_critical_injury_insurance.png",
  "Performance Bonus & Incentive": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/1jogw8il_performance_bonus_incentive.png",
  "Athlete Transfer Insurance": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/p6vbypw1_athlete_transfer_insurance.png",
  "Disgrace Insurance": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/bsicizk7_disgrace_insurance.png",
  "Downside & Revenue Protection": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/88fihd1x_downside_revenue_protection.png",
  // K-12 — Lines of Coverage
  "Student Accident Insurance": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/nfetne48_student_accident_insurance.png",
  "Interscholastic Sports Coverage": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/kvgeei23_interscholastic_sports_coverage.png",
  "Catastrophic / Umbrella Coverage": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/i6rykbjn_catastrophic_umbrella_coverage.png",
  "Special Risk Insurance": "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/sqxk6e3y_special_risk_insurance.png",
};

const CardIcon = ({ label }) => (
  ICONS[label]
    ? <div className="icon-wrap icon-wrap-img" data-testid={`icon-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><img src={ICONS[label]} alt="" loading="lazy" /></div>
    : <div className="icon-wrap">●</div>
);

const SimplePage = ({ eyebrow, title, body, img, sections = [], primaryHref, primaryCta }) => (
  <>
    <Hero eyebrow={eyebrow} title={title} body={body} img={img} {...(primaryHref ? { primaryHref } : {})} {...(primaryCta ? { primaryCta } : {})} />
    {sections.map((s, i) => (
      <Section key={i} alt={i % 2 === 0}>
        {s.spotlight ? (
          <div className="spotlight-card" data-testid={s.spotlight.testid || "spotlight"}>
            <div className="spotlight-copy">
              <span className="eyebrow">{s.eyebrow}</span>
              <h2 className="display display-m mt-6">{s.title}</h2>
              <p className="spotlight-lede">{s.spotlight.summary}</p>
              <p className="spotlight-source">{s.spotlight.source}</p>
            </div>
            <a className="btn btn-primary spotlight-cta" href={s.spotlight.href} target="_blank" rel="noopener noreferrer" data-testid={(s.spotlight.testid || "spotlight") + "-cta"}>
              {s.spotlight.cta || "Read the article"} →
            </a>
          </div>
        ) : (
          <>
            <div className="sec-head">
              <div>
                <span className="eyebrow">{s.eyebrow}</span>
                <h2 className="display display-m mt-6">{s.title}</h2>
              </div>
              {s.body && <p className="lede">{s.body}</p>}
            </div>
            {s.cards && (
              <div className={s.grid || "split-3"}>
                {s.cards.map(([h,p]) => (
                  <div key={h} className="feature-card"><CardIcon label={h} /><h3>{h}</h3><p>{p}</p></div>
                ))}
              </div>
            )}
            {s.listRows && (
              <div className="list-rows" data-testid="lines-of-coverage-list">
                {s.listRows.map(([h, p], idx) => (
                  <div key={h} className="list-row">
                    <div className="list-row-num">{String(idx + 1).padStart(2, "0")}</div>
                    <div className="list-row-body">
                      <h4>{h}</h4>
                      <p>{p}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Section>
    ))}
    <FooterCTA />
  </>
);

const NEWS_POSTS = [
  { slug: "i-am-alive-because-someone-gave-me-a-game-to-play", title: "I Am Alive Because Someone Gave Me a Game to Play", tag: "Athlete Safety", desc: "A first-person reminder from Tyrre Burks on why access to sport is a safety issue — and why we do this work.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/p3t7q3jc_Founder_Letter_Hero_Recreated.jpg" },
  { slug: "players-health-launches-the-sports-desert-index", title: "The Sports Desert Index", tag: "Youth", desc: "A first-of-its-kind interactive study mapping America's youth sports access crisis, city by city — 55 cities, 50 states, 6 weighted indicators.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/cfzp9v08_Screenshot%202026-07-20%20at%201.54.16%E2%80%AFPM.webp", featured: true, featuredLabel: "Interactive Research", featuredCta: "Explore the map" },
  { slug: "players-health-and-maxu-go-all-in-on-the-mental-game", title: "Players Health and MaxU Go All In on the Mental Game", tag: "Athlete Safety", desc: "Expanding a national partnership to make mental performance training part of the standard of athlete safety.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ody96jia_maxu-mental-game_4.webp" },
  { slug: "players-health-foundation-launches-100-000-grant-program-to-protect-young-athletes", title: "Players Health Foundation Launches $100,000 Grant Program to Protect Young Athletes", tag: "Youth", desc: "Twenty $5,000 grants funding youth sports organizations committed to safety and access.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/kg4ybujj_foundation-grant_3.webp" },
  { slug: "the-next-era-of-nil-is-about-managing-risk", title: "The Next Era of NIL Is About Managing Risk", tag: "Collegiate", desc: "Why the next competitive edge in college athletics is disciplined risk management, not just spending.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/y975dacl_nil-managing-risk_2.webp" },
  { slug: "creating-a-positive-sport-environment", title: "Creating a Positive Sport Environment", tag: "Youth", desc: "Practical steps for building a culture of safety, respect, and enjoyment in youth sports.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/5bmwvr11_positive-sport-environment.webp" },
  { slug: "zurich-players-health-offer-injury-insurance-for-nil-era-of-college-sports", title: "Zurich + Players Health Offer Injury Insurance for the NIL Era of College Sports", tag: "Collegiate", desc: "A first-of-its-kind Critical Injury Protection policy for NIL commitments.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/4kvvnzyc_zurich-nil-injury-insurance_2.webp" },
  { slug: "ankored-partners-with-players-health-to-streamline-youth-sports-compliance", title: "Ankored Partners with Players Health to Streamline Youth Sports Compliance", tag: "Youth", desc: "A new integration bringing background checks, training, and compliance tracking into one workflow.", image: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/1nxu0std_ankored-compliance_2.webp" },
  { slug: "bfit-builds-a-culture-of-proactive-risk-management-with-players-health", title: "BFit Builds a Culture of Proactive Risk Management with Players Health", tag: "Fitness", desc: "Club Solutions Magazine covers how BFit is making proactive risk management part of its culture.", url: "https://clubsolutionsmagazine.com/2026/09/bfit-builds-a-culture-of-proactive-risk-management-with-players-health/" },
  // Placeholder for "Out of Bounds" documentary — will populate when press release is ready
];

const GUIDEBOOKS = [
  { id: "expert-playbook-athlete-safety", title: "Expert Playbook: Athlete Safety, Risk & Insurance (Updated April 2026)", tag: "Athlete Safety" },
  { id: "abuse-prevention", title: "Coaches Guidebook to Abuse Prevention", tag: "Athlete Safety" },
  { id: "expert-playbook-fitness", title: "Expert Playbook: Member Safety and Risk Management for the Fitness Industry", tag: "Fitness" },
  { id: "safeguarding-club-funds", title: "Safeguarding Your Club's Funds", tag: "Youth" },
  { id: "heat-illness", title: "Heat Illness Guidebook", tag: "Athlete Safety" },
  { id: "concussion", title: "Concussion Guidebook", tag: "Athlete Safety" },
  { id: "state-law-guide", title: "Concussion, Cardiac Arrest & Background Check State Law Guide", tag: "Athlete Safety" },
  { title: "ACL Prevention Field Guide", tag: "Athlete Safety", hidden: true, url: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/4dujd9h8_acl-prevention-field-guide.pdf" },
  { title: "Goal and Hoop Anchoring Guide", tag: "Athlete Safety", hidden: true, url: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/ymuoprxs_goal-and-hoop-anchoring-guide.pdf" },
  { title: "Inhalers & Epinephrine: A Coach's Guide", tag: "Athlete Safety", hidden: true, url: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/bab0z8pe_inhalers-epinephrine-coach-guide.pdf" },
  { title: "Premises Standard", tag: "Athlete Safety", hidden: true, url: "https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/slfo1qic_premises-standard.pdf" },
];

const tagSlug = t => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const TagChip = ({ tag }) => (
  <span className={`tag-chip tag-${tagSlug(tag)}`} data-testid={`tag-${tagSlug(tag)}`}>{tag}</span>
);

const NEWS_CATEGORIES = ["All", "Youth", "Collegiate", "Fitness", "Athlete Safety"];

const ResourcesPage = () => {
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? NEWS_POSTS : NEWS_POSTS.filter(p => p.tag === filter);
  return (
  <>
    <Hero
      eyebrow="Resources"
      title="The playbook before the incident."
      body="Everything here answers one question: where is your program exposed, and what do you do about it? Built by the team that insures sports for a living."
      img={ATHLETE_IMG.soccer}
    />
    <Section>
      <div className="sec-head">
        <div>
          <span className="eyebrow">Library</span>
          <h2 className="display display-l mt-6">Built by sports people, for sports people.</h2>
        </div>
      </div>
    </Section>

    <Section alt tight>
      <div className="cat-head">
        <span className="eyebrow">01 — News, Articles & Blog Posts</span>
        <h3 className="display display-s mt-6">The latest from Players Health.</h3>
      </div>
      <div className="filter-bar" role="tablist" aria-label="Filter articles by category" data-testid="news-filter-bar">
        {NEWS_CATEGORIES.map(cat => {
          const count = cat === "All" ? NEWS_POSTS.length : NEWS_POSTS.filter(p => p.tag === cat).length;
          const active = filter === cat;
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={active}
              className={`filter-chip${active ? " is-active" : ""}`}
              onClick={() => setFilter(cat)}
              data-testid={`news-filter-${tagSlug(cat)}`}
            >
              {cat} <span className="filter-count">{count}</span>
            </button>
          );
        })}
      </div>
      <div className="post-grid" data-testid="news-post-list">
        {filtered.length === 0 ? (
          <p className="lede" data-testid="news-empty">No articles in this category yet.</p>
        ) : (() => {
          const featured = filtered.find((p) => p.featured);
          const regular = filtered.filter((p) => !p.featured);
          return (
            <>
              {featured ? (
                <a
                  href={`/resources/${featured.slug}`}
                  className="post-card post-card-featured"
                  data-testid={`post-featured-${featured.slug}`}
                >
                  <div className={`post-card-media post-card-media-featured tag-media-${tagSlug(featured.tag)}`}>
                    {featured.image ? <img src={featured.image} alt="" className="post-card-art" loading="lazy" decoding="async" /> : null}
                    <span className="post-card-featured-badge">{featured.featuredLabel || "Featured"}</span>
                  </div>
                  <div className="post-card-body post-card-body-featured">
                    <TagChip tag={featured.tag} />
                    <h3>{featured.title}</h3>
                    <p>{featured.desc}</p>
                    <span className="post-card-cta">{featured.featuredCta || "Read more"} <span className="arr">→</span></span>
                  </div>
                </a>
              ) : null}
              <div className="post-card-grid">
                {regular.map((p) => {
                  const i = NEWS_POSTS.indexOf(p);
                  return (
                    <a
                      key={i}
                      href={p.url || `/resources/${p.slug}`}
                      target={p.url ? "_blank" : undefined}
                      rel={p.url ? "noopener noreferrer" : undefined}
                      className="post-card"
                      data-testid={`post-${i}`}
                    >
                      <div className={`post-card-media tag-media-${tagSlug(p.tag)}`}>
                        {p.image ? <img src={p.image} alt="" className="post-card-art" loading="lazy" decoding="async" /> : (
                          <span className="post-card-placeholder-eyebrow">{p.tag}</span>
                        )}
                      </div>
                      <div className="post-card-body">
                        <TagChip tag={p.tag} />
                        <h4>{p.title}</h4>
                        <p>{p.desc}</p>
                        <span className="post-card-cta">Read <span className="arr">→</span></span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>
    </Section>

    <Section>
      <div className="cat-head">
        <span className="eyebrow">02 — Guidebooks</span>
      </div>
      <a href="/guidebooks" className="teaser-card teaser-cool" data-testid="teaser-guidebooks">
        <div className="teaser-copy">
          <h3 className="display display-m">Practical guides for safer programs.</h3>
          <p className="lede on-dark">Camp safety, concussion protocols, background checks, abuse prevention — depth on the topics that matter most.</p>
          <span className="btn btn-primary" style={{ marginTop: 12 }}>Explore guidebooks →</span>
        </div>
      </a>
    </Section>
    <FooterCTA />
  </>
  );
};

const GuidebooksPage = () => {
  const gate = useGuidebookGate();
  return (
  <>
    <Hero
      eyebrow="Guidebooks"
      title="Guidebooks for every risk you actually face."
      body="Free, sports-specific playbooks written by the team that insures sports for a living. Download what you need — send it to your board, your coaches, and your parents."
      img={ATHLETE_IMG.tennis}
    />
    <Section>
      <div className="sec-head">
        <div>
          <span className="eyebrow">The Library</span>
          <h2 className="display display-l mt-6">Practical, printable, program-ready.</h2>
        </div>
        <p className="lede">Every guidebook is written for the person actually running the program — not the lawyer down the hall.</p>
      </div>
      <div className="guidebook-grid" data-testid="guidebook-grid">
        {GUIDEBOOKS.filter(g => !g.hidden).map((g, i) => (
          <button
            key={g.id}
            type="button"
            onClick={() => gate.open(g)}
            className="guidebook-card has-pdf"
            data-testid={`guidebook-${i}`}
          >
            <div className="guidebook-num">G{String(i + 1).padStart(2, "0")}</div>
            <TagChip tag={g.tag} />
            <h4>{g.title}</h4>
            <span className="guidebook-cta">{gate.busyId === g.id ? "Opening…" : "Download PDF →"}</span>
          </button>
        ))}
      </div>
      <Reveal className="compliance-footnote" data-testid="guidebooks-access-line">
        <p>All insureds get access to hundreds of policy templates, tutorials, and guidebooks upon binding.</p>
      </Reveal>
    </Section>
    <FooterCTA />
    {gate.active && <GuidebookGateModal guidebook={gate.active} onClose={gate.close} />}
  </>
  );
};

const CoursesPage = () => (
  <>
    <Hero
      eyebrow="Courses"
      title="Training built for safer sports."
      body="SafeSport-aligned courses for coaches, staff, and volunteers. Assign, track completion, and stay audit-ready — all from one dashboard."
      img={ATHLETE_IMG.basketball}
    />
    <Section>
      <div className="sec-head">
        <div>
          <span className="eyebrow">Course Catalog</span>
          <h2 className="display display-l mt-6">Standards-aligned. Sport-specific.</h2>
        </div>
        <p className="lede">Every course maps to a compliance requirement or a real-world risk we see in claims data.</p>
      </div>
      <div className="split-3">
        {[
          ["Abuse Prevention Essentials","Required foundation for every adult in the program.","60 min"],
          ["Concussion Recognition","Sideline protocols for coaches and staff.","45 min"],
          ["Emergency Action Planning","Build a plan for cardiac, weather, and violence events.","50 min"],
          ["Mental Health First Aid","Recognize and respond to athletes in crisis.","90 min"],
          ["SafeSport for Youth Coaches","The baseline every youth org should require.","75 min"],
          ["Facility Risk Walkthrough","Turn a walkthrough into a documented risk plan.","30 min"],
        ].map(([h,p,dur], i) => (
          <a
            key={h}
            href="https://onlinelearning-playershealth.talentlms.com/plus/"
            target="_blank"
            rel="noopener noreferrer"
            className="feature-card course-card"
            data-testid={`course-${i}`}
          >
            <div className="card-num">{dur}</div>
            <h3>{h}</h3>
            <p>{p}</p>
            <span className="guidebook-cta">Learn more →</span>
          </a>
        ))}
      </div>
    </Section>
    <FooterCTA />
  </>
);
const AboutPage = () => (
  <>
    <Hero
      eyebrow="About Players Health"
      title="We exist to make sports as safe as they are great."
      body="The insurance and safety platform for youth, amateur, fitness, and collegiate sports — covering the leagues, clubs, studios, and schools that move kids and adults onto the field."
      img={ATHLETE_IMG.tennis}
    />

    <Section>
      <div className="sec-head">
        <div>
          <span className="eyebrow">The Industry</span>
          <h2 className="display display-l mt-6">Sports is one of the largest, least protected industries in the country.</h2>
        </div>
        <p className="lede">Millions of programs run every season without a clear picture of their risk — and the carriers built for them are stretched thin. We built Players Health to close that gap with real coverage and real expertise.</p>
      </div>
      <div className="split-2">
        <div className="stat-card" data-testid="stat-insured">
          <div className="stat-card-value">100+ Million</div>
          <div className="stat-card-label">Insured</div>
        </div>
        <div className="stat-card" data-testid="stat-carriers">
          <div className="stat-card-value">A-Rated</div>
          <div className="stat-card-label">Carriers</div>
        </div>
      </div>
    </Section>

    <Section alt>
      <div className="sec-head">
        <div>
          <span className="eyebrow">Our Mission</span>
          <h2 className="display display-l mt-6">Safety as a standard, not an afterthought.</h2>
        </div>
      </div>
      <div className="split-3">
        {[
          ["Player-First", "Every decision starts with the athlete."],
          ["Tech + Touch", "Modern tools, real humans on the phone."],
          ["Carrier-Backed", "Top-rated carriers, transparent pricing."],
        ].map(([h, p]) => (
          <div key={h} className="feature-card"><CardIcon label={h} /><h3>{h}</h3><p>{p}</p></div>
        ))}
      </div>
    </Section>

    <Section dark>
      <div className="promise-block" data-testid="about-promise">
        <span className="eyebrow on-dark">Our Promise</span>
        <h2 className="display display-m mt-6" style={{ color: "#fff" }}>Every policy comes with a plan — not just a certificate.</h2>
        <p className="lede on-dark mt-6">We don't just insure your program; we help you see your risk and reduce it, so fewer athletes get hurt in the first place.</p>
      </div>
    </Section>

    <Section>
      <div className="letter" data-testid="founder-letter">
        <span className="eyebrow">A Letter From Our Founder</span>
        <p className="letter-quote mt-6">"I am alive because someone gave me a game to play."</p>
        <p className="letter-attr">Tyrre Burks, Founder &amp; CEO, Players Health</p>
        <div className="letter-body mt-8">
          <p>Where I grew up, the odds didn't bend in my favor. What stood between me and the futures I could have had wasn't luck — it was a field, a whistle, a team that expected me to show up. A coach named Fredrick Ward saw something in me before I could see it myself. A mother who sold cookies in the stands, dollar by dollar, so our team could have what it needed to play.</p>
          <p>More than 60 million children play youth sports in this country. Every one of them is learning something that will outlast the final whistle. When a community loses access to sports, it isn't just losing games — it's losing the coaches, the mentors, the belonging, and the second chances that turn a life around. I know, because those are the exact things that saved mine.</p>
          <p>That's why we built Players Health, and why we built the Players Health Foundation: to make sure the next kid who needs a game to play actually finds one.</p>
        </div>
        <a
          href="https://playershealthfoundation.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="foundation-callout mt-8"
          data-testid="about-foundation-link"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_webpage-craft-59/artifacts/8jyho30v_image.png"
            alt="Players Health Foundation"
            className="foundation-logo"
            loading="lazy"
            decoding="async"
          />
          <div className="foundation-copy">
            <span className="eyebrow">The Foundation</span>
            <p className="foundation-lede">Fueling access to sport for the next generation of athletes.</p>
          </div>
          <span className="foundation-cta">Visit the Players Health Foundation →</span>
        </a>
      </div>
    </Section>
    <FooterCTA />
  </>
);

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
  const [status, setStatus] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", { form_name: "contact_us" });
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", organization: "", message: "" });
    } catch { setStatus("error"); }
  };
  return (
    <>
      <section className="hero-dark">
        <div className="hero-motif" />
        <div className="hero-glow" aria-hidden="true" />
        <div className="container">
          <div className="hero-grid page-fade" style={{ alignItems: "start" }}>
            <div className="hero-copy">
              <span className="eyebrow on-dark">Support</span>
              <MaskedTitle text="Talk to a sports specialist." className="display display-xl" testid="contact-hero-title" />
              <p className="lede on-dark">Real people who know sports — quote questions, claims help, and policy support. Tell us what you need and we'll follow up fast.</p>
            </div>
            <div className="contact-form-card" data-testid="contact-form-card">
              {status === "sent" ? (
                <div data-testid="contact-success" style={{ padding: 24, background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 12, color: "#065F46" }}>
                  <strong>Message received.</strong> A sports specialist will reach out within one business day.
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <div className="form-row"><label>Name</label><input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="contact-name" /></div>
                  <div className="form-row"><label>Email</label><input required type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="contact-email" /></div>
                  <div className="form-row"><label>Phone</label><input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} data-testid="contact-phone" /></div>
                  <div className="form-row"><label>Organization Name</label><input value={form.organization} onChange={e=>setForm({...form, organization: e.target.value})} data-testid="contact-org" /></div>
                  <div className="form-row"><label>How can we help?</label><textarea required rows={5} value={form.message} onChange={e=>setForm({...form, message: e.target.value})} data-testid="contact-message" /></div>
                  <button type="submit" disabled={status==="sending"} className="btn btn-accent" style={{ width: "100%" }} data-testid="contact-submit">
                    {status === "sending" ? "Sending…" : "Talk to Us"}
                  </button>
                  {status === "error" && <p style={{ color: "#FCA5A5", marginTop: 12, fontSize: 14 }}>Something went wrong. Try again or email help@playershealth.com.</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="sec-head">
          <div>
            <span className="eyebrow">Get In Touch</span>
            <h2 className="display display-l mt-6">We're here when you need us.</h2>
          </div>
        </div>
        <div className="split-3">
          {[
            ["Sales", "help@playershealth.com"],
            ["Claims", "claims@playershealth.com"],
            ["Support", "help@playershealth.com"],
          ].map(([h, mail]) => (
            <a key={h} href={`mailto:${mail}`} className="feature-card contact-card" data-testid={`contact-cat-${h.toLowerCase()}`}>
              <div className="card-num">{h}</div>
              <h3 className="contact-email">{mail}</h3>
            </a>
          ))}
        </div>
      </Section>
      <FooterCTA />
    </>
  );
};
const AthleteSafetyPage = () => (
  <>
    <section className="hero-dark">
      <div className="hero-motif" />
      <div className="hero-glow" aria-hidden="true" />
      <div className="container">
        <div className="hero-grid page-fade">
          <div className="hero-copy">
            <span className="eyebrow on-dark" data-testid="safety-hero-eyebrow">Athlete and Member Safety</span>
            <MaskedTitle text="Safety is the difference." className="display display-xl" testid="safety-hero-title" />
            <p className="lede on-dark" data-testid="safety-hero-body">Most MGAs price off loss history alone. We use real safety data and real claims data together, so your clients see exactly where their risk sits and get the tools to reduce it. Safer athletes and members, better pricing, stickier accounts.</p>
            <div className="hero-actions">
              <MagneticButton href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="safety-hero-cta">Get a Quote</MagneticButton>
              <a href="/support" className="btn btn-ghost" data-testid="safety-hero-talk">Talk to us</a>
            </div>
          </div>
          <div className="hero-photo"><img src={ATHLETE_IMG.safety} alt="" width="1200" height="1500" fetchPriority="high" decoding="async" /></div>
        </div>
      </div>
    </section>

    <Section noReveal>
      <Reveal className="sec-head">
        <div>
          <span className="eyebrow">How we underwrite safety</span>
          <h2 className="display display-l mt-6">What makes us different.</h2>
        </div>
        <p className="lede">Safety is not a program we bolt on. It is how we underwrite.</p>
      </Reveal>
      <Stagger className="split-4" data-testid="safety-difference-grid">
        {[
          ["01", "Priced on more than loss history", "We look at how an organization actually operates, not just what happened to them last year, so the ones doing the work get recognized for it."],
          ["02", "Risk your clients can see", "Every insured gets a clear read on where they are strong and where they are exposed, in language they understand and can act on."],
          ["03", "Rewarded for doing the work", "Organizations that put real safety practices in place and keep them in place see it reflected at renewal."],
          ["04", "Fewer injuries, better outcomes", "Prevention that works shows up twice, in healthier athletes and members and in a better loss picture for the account."],
        ].map(([num, h, p]) => (
          <StaggerItem key={h} className="feature-card feature-card-numbered" data-testid={`safety-diff-${num}`}>
            <div className="card-num">{num}</div>
            <h3>{h}</h3>
            <p>{p}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>

    <Section dark>
      <div className="split-2" style={{ alignItems: "start" }}>
        <div>
          <span className="eyebrow on-dark">Safety Score</span>
          <h2 className="display display-l mt-6" style={{ color: "#fff" }}>Every account gets a Safety Score.</h2>
        </div>
        <p className="lede on-dark" data-testid="safety-score-body" style={{ maxWidth: 620 }}>
          The Safety Score shows your client where their safety practices hold up and where they are exposed. It gives you a real reason to be in front of them between renewals, with something useful to say.
        </p>
      </div>
    </Section>

    <Section noReveal alt>
      <Reveal className="sec-head">
        <div>
          <span className="eyebrow">What's included</span>
          <h2 className="display display-l mt-6">A complete safety stack.</h2>
        </div>
      </Reveal>
      <Stagger className="split-3" data-testid="safety-stack-grid">
        {[
          ["Safety Score","A clear read on where the program stands today and where it is exposed."],
          ["Claims Insight","Their own loss picture in plain language, and what it says about where to focus."],
          ["Athlete Injury Prevention","Knee injuries, overuse, and contact injuries drive youth and amateur sport claims. Guidance built around bringing them down."],
          ["Member Injury Prevention","Slips, trips, and falls, equipment, and facility conditions drive nearly every fitness claim. We show where they come from and how to reduce them."],
          ["Prevention Standards","Field guides and standards your clients can put in place without hiring anyone."],
          ["Renewal Impact","Safety practices that hold up over time show up in the renewal."],
        ].map(([h, p]) => (
          <StaggerItem key={h} className="feature-card" data-testid={`safety-stack-${h.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")}`}>
            <h3>{h}</h3>
            <p>{p}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>

    <Section dark>
      <div className="split-2" style={{ alignItems: "start" }}>
        <div>
          <span className="eyebrow on-dark">Recalibration</span>
          <h2 className="display display-l mt-6" style={{ color: "#fff" }}>A program that keeps getting sharper.</h2>
        </div>
        <p className="lede on-dark" style={{ maxWidth: 620 }} data-testid="safety-recalibration-body">
          We review what we are seeing across the book on a regular cadence and refine the program from it. Pricing gets more precise and risk guidance gets more useful over time, which means your clients are working with an MGA that does not stand still.
        </p>
      </div>
    </Section>

    <section className="footer-cta">
      <Reveal className="container footer-cta-inner">
        <div>
          <h2 className="display display-m" style={{ color: "#fff", margin: 0 }}>Place your next account with an MGA that underwrites safety.</h2>
          <p className="lede on-dark mt-6">Get appointed and see the difference on your next submission.</p>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <MagneticButton href="/brokers" className="btn btn-primary" data-testid="safety-cta-appointed">Get Appointed</MagneticButton>
          <a href="/support" className="btn btn-ghost" data-testid="safety-cta-talk">Talk to us</a>
        </div>
      </Reveal>
    </section>
  </>
);



const BrokerPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", agency: "", states: "", message: "" });
  const [files, setFiles] = useState({ w9: null, eo_dec: null, licenses: null });
  const [status, setStatus] = useState("idle");
  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", agency: "", states: "", message: "" });
    setFiles({ w9: null, eo_dec: null, licenses: null });
  };
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (files.w9) data.append("w9", files.w9);
      if (files.eo_dec) data.append("eo_dec", files.eo_dec);
      if (files.licenses) data.append("licenses", files.licenses);
      await axios.post(`${API}/broker-appointment`, data);
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", { form_name: "get_appointed" });
      setStatus("sent");
      resetForm();
    } catch { setStatus("error"); }
  };
  return (
    <>
      <section className="hero-dark">
        <div className="hero-motif" />
        <div className="hero-glow" aria-hidden="true" />
        <div className="container">
          <div className="hero-grid page-fade">
            <div className="hero-copy">
              <span className="eyebrow on-dark">Brokers &amp; Agents</span>
              <MaskedTitle text="A smarter way to place coverage." className="display display-xl" testid="broker-hero-title" />
              <p className="lede on-dark">Insurance built for sport and fitness, tools that show your clients exactly where their risk sits, and a quote to bind process that never slows you down. We make it easy to win the account and easy to keep it.</p>
              <div className="hero-actions">
                <a href="/brokers" onClick={(e) => { e.preventDefault(); document.getElementById("appointment")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="btn btn-primary" data-testid="broker-hero-cta">Get Appointed</a>
                <a href="/support" className="btn btn-ghost">Talk to us</a>
              </div>
            </div>
            <div className="hero-photo"><img src={ATHLETE_IMG.brokers} alt="" fetchPriority="high" decoding="async" /></div>
          </div>
        </div>
      </section>

      <Section noReveal>
        <Reveal className="sec-head">
          <div>
            <span className="eyebrow">Broker Appetite</span>
            <h2 className="display display-l mt-6">Where we play — and where to call your underwriter.</h2>
          </div>
          <p className="lede">A working framework, not a binding appetite. Every account is reviewed individually; classes outside the broadest categories are routinely written when the file supports it.</p>
        </Reveal>
        <Reveal className="callout-box" data-testid="appetite-callout">
          <strong>Read as guidance</strong>
          <p>These categories are starting points for the conversation. Loss history, safety protocols, exposure controls and program design all weigh into the final underwriting decision — referrals and exceptions are welcome.</p>
        </Reveal>
        <Stagger className="split-3" data-testid="appetite-classes">
          {[
            { cls: "A", accent: "teal", label: "Broadest — Low Hazard", body: "Our deepest capacity. Fast indications for in-appetite submissions across endurance, racquet, fitness and no-contact formats.", cats: "Endurance & running, racquet sports, golf, dance & yoga, fitness & personal training, cricket, curling & low-contact team formats." },
            { cls: "B", accent: "yellow", label: "Core Book — Medium Hazard", body: "Active appetite across the core of amateur sport with structured safety protocols and credible loss history.", cats: "Baseball & softball, basketball, soccer, volleyball, cheerleading, non-contact football & ice hockey, swimming & martial arts, weightlifting." },
            { cls: "C", accent: "orange", label: "Refer Early — High Hazard", body: "Considered case-by-case. Best fit with documented protocols, mature programs and a clean loss history — bring underwriting in early.", cats: "Contact football, ice & inline hockey, lacrosse, rugby, wrestling, gymnastics & trampoline, downhill skiing & snowboarding, skateboarding." },
          ].map(a => (
            <StaggerItem key={a.cls} className={`feature-card appetite-card appetite-${a.accent}`} data-testid={`appetite-class-${a.cls}`}>
              <div className="appetite-bar" aria-hidden="true" />
              <div className="card-num">Class {a.cls}</div>
              <h3>{a.label}</h3>
              <p>{a.body}</p>
              <p className="appetite-cats"><strong>Categories:</strong> {a.cats}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <Section alt noReveal>
        <Reveal className="sec-head">
          <div>
            <span className="eyebrow">Lines & Limits</span>
            <h2 className="display display-l mt-6">Coverage offered — placed through admitted A-rated carriers.</h2>
          </div>
        </Reveal>
        <Stagger className="split-3" data-testid="lines-limits-grid">
          {[
            ["Commercial General Liability","Sirius Point / Spinnaker","Up to $5M Occurrence / $5M Aggregate. Products & Completed Ops up to $3M. Participant Liability up to $1M / $1M."],
            ["Participant Accident","Sirius Point","$25,000 minimum, amateur sport. Limits available up to $100,000. Excess medical, AD&D, dental."],
            ["Sexual Abuse & Molestation","GL sub-limit","Sub-limit on the GL policy. Up to $1M Occ / $2M Aggregate. Paired with SAM protocol library."],
            ["Commercial Property","Spinnaker / GAIC / Aspen (new)","Spinnaker up to $5M TIV/location. Great American up to $1M TIV. Aspen Inland Marine up to $6M TIV."],
            ["Fitness Specialty Lines","Studio & club endorsements","Professional Liability to limit. Hired & Non-Owned Auto up to $1M. Medical Payment up to $25,000."],
            ["One Submission · One Bind","All lines","Cross-line app, shared exposure data. Single quote & subjectivities. One underwriter relationship."],
          ].map(([h, sub, p]) => (
            <StaggerItem key={h} className="feature-card">
              <div className="card-num" style={{ fontSize: 11 }}>{sub}</div>
              <h3>{h}</h3>
              <p>{p}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <Section noReveal>
        <Reveal className="sec-head">
          <div>
            <span className="eyebrow">New Lines · 2026 <span className="launch-badge">7/1 Launch</span></span>
            <h2 className="display display-l mt-6">XS GL & commercial property.</h2>
          </div>
          <p className="lede">Two new lines rounding out the program — Excess General Liability on non-admitted paper, and Commercial Property on admitted A-rated paper. One submission, one underwriting relationship.</p>
        </Reveal>
        <Stagger className="split-2" data-testid="new-lines-grid">
          {[
            ["Line 01 · Non-Admitted","Excess General Liability","Non-admitted Excess GL capacity for amateur sport and facilities, written alongside Participant Accident and SAM under one program. Occurrence up to $5M. Aggregate up to $5M. Products & Completed Ops up to $3M."],
            ["Line 02 · Admitted A-Rated","Commercial Property","Building, BPP and inland-marine capacity for sports and fitness facilities — small studios through multi-location operations. Spinnaker (in-house) up to $5M TIV. Great American up to $1M TIV. Aspen Inland Marine up to $6M TIV."],
          ].map(([sub, h, p]) => (
            <StaggerItem key={h} className="feature-card">
              <div className="card-num" style={{ fontSize: 11 }}>{sub}</div>
              <h3>{h}</h3>
              <p>{p}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>


      <Section>
        <div className="sec-head">
          <div>
            <span className="eyebrow">Broker Tools</span>
            <h2 className="display display-l mt-6">Built for producers.</h2>
          </div>
          <p className="lede">Everything you need to compete for sports business — with a market that finally understands the risk.</p>
        </div>
        <div className="split-3">
          {[
            ["Instant Quotes", "Quote in minutes, not days."],
            ["Digital Bind", "E-sign and issue without paperwork."],
            ["Commission Portal", "Track and reconcile in one place."],
          ].map(([h, p]) => (
            <div key={h} className="feature-card"><CardIcon label={h} /><h3>{h}</h3><p>{p}</p></div>
          ))}
        </div>
      </Section>

      <section id="appointment" className="section section-alt" data-testid="broker-appointment-section">
        <div className="container">
          <div className="hero-grid" style={{ alignItems: "start", padding: 0 }}>
            <div className="hero-copy">
              <span className="eyebrow">Broker Appointment</span>
              <h2 className="display display-l">Get appointed with Players Health.</h2>
              <p className="lede">Tell us a bit about your agency and the states you write in. A broker success lead will follow up within one business day with next steps and commission details.</p>
              <ul className="broker-perks mt-8">
                <li>Direct access to sports-specialized underwriters</li>
                <li>Competitive commissions and clear reconciliation</li>
                <li>Full digital quote-to-bind workflow</li>
              </ul>
            </div>
            <div className="contact-form-card" style={{ background: "#fff", boxShadow: "0 24px 48px -20px rgba(15,23,42,0.15)" }}>
              {status === "sent" ? (
                <div data-testid="broker-success" style={{ padding: 24, background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 12, color: "#065F46" }}>
                  <strong>Request received.</strong> A broker success lead will reach out within one business day.
                </div>
              ) : (
                <form onSubmit={submit} data-testid="broker-form">
                  <div className="form-row"><label>Name</label><input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="broker-name" /></div>
                  <div className="form-row"><label>Email</label><input required type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="broker-email" /></div>
                  <div className="form-row"><label>Phone</label><input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} data-testid="broker-phone" /></div>
                  <div className="form-row"><label>Agency Name</label><input required value={form.agency} onChange={e=>setForm({...form, agency: e.target.value})} data-testid="broker-agency" /></div>
                  <div className="form-row"><label>States Licensed In</label><input value={form.states} onChange={e=>setForm({...form, states: e.target.value})} placeholder="e.g. NY, NJ, PA" data-testid="broker-states" /></div>
                  <div className="form-row"><label>Tell us about your book</label><textarea rows={4} value={form.message} onChange={e=>setForm({...form, message: e.target.value})} data-testid="broker-message" /></div>
                  <div className="form-row-files">
                    <p className="form-files-note">Optional — attach if available. Accepted: PDF, JPG, PNG (max 8 MB each).</p>
                    {[
                      { key: "w9", label: "W9" },
                      { key: "eo_dec", label: "E&O Dec Page" },
                      { key: "licenses", label: "Agency Licenses" },
                    ].map(({ key, label }) => (
                      <div key={key} className="form-file">
                        <label htmlFor={`broker-file-${key}`} className="form-file-label">
                          <span className="form-file-title">{label}</span>
                          <span className="form-file-pick">{files[key] ? "Change file" : "Choose file"}</span>
                        </label>
                        <input
                          id={`broker-file-${key}`}
                          type="file"
                          accept="application/pdf,image/jpeg,image/png"
                          onChange={e => setFiles(f => ({ ...f, [key]: e.target.files?.[0] || null }))}
                          data-testid={`broker-file-${key}`}
                        />
                        {files[key] && (
                          <div className="form-file-selected">
                            <span className="form-file-name">{files[key].name}</span>
                            <button type="button" className="form-file-remove" onClick={() => setFiles(f => ({ ...f, [key]: null }))} data-testid={`broker-file-${key}-remove`} aria-label={`Remove ${label}`}>×</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <button type="submit" disabled={status==="sending"} className="btn btn-accent" style={{ width: "100%" }} data-testid="broker-submit">
                    {status === "sending" ? "Sending…" : "Get Appointed"}
                  </button>
                  {status === "error" && <p style={{ color: "#B91C1C", marginTop: 12, fontSize: 14 }}>Something went wrong. Try again or email help@playershealth.com.</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <FooterCTA />
    </>
  );
};

const PAGES = {
  "/insurance": <SimplePage eyebrow="Insurance" title="Coverage built for sports." body="Modular policies that match how your program actually runs — from a single team to a national league."
    img={ATHLETE_IMG.resources}
    sections={[{ eyebrow: "Lines of Coverage", title: "Everything in one place.", cards: [["General Liability","Foundation protection for third-party claims."],["Accident","Primary and excess medical for participants."],["D&O","Defend leadership decisions."],["Equipment","Gear, uniforms, and facility property."],["Crime","Theft and embezzlement protection."],["Cyber","Registration platforms and donor data."]]}]} />,
  "/athlete-safety": <SimplePage eyebrow="Athlete Safety" title="Safety as a standard." body="Background checks, SafeSport training, abuse-prevention policies, and incident reporting — wired into the way you operate."
    img={ATHLETE_IMG.youth}
    sections={[{ eyebrow: "What's Included", title: "A complete safety stack.", cards: [["Background Checks","Continuous monitoring for coaches and staff."],["SafeSport Training","Required courses tracked automatically."],["Incident Reporting","Anonymous and named reporting in one workflow."],["Policy Library","SafeSport-aligned templates ready to deploy."],["Risk Dashboards","See exposure across teams in real time."],["Compliance Support","People who help you stay audit-ready."]]}]} />,
  "/youth-sports": <SimplePage eyebrow="Youth Sports" title="Protect every kid on the field." body="Insurance built for youth sports, visibility into your program's risk, and the tools to keep every kid safer — from small clubs to national governing bodies."
    img={ATHLETE_IMG.youthSports} sections={[
      { eyebrow: "Why Players Health", title: "Built around youth programs.", cards: [["Sport-Specific","Coverage tuned to the realities of each sport."],["Safety Insight","Insight into where athlete safety is holding up in your program and where it isn't, so you can act before someone gets hurt."],["Affordable","Group pricing that respects nonprofit budgets."]] },
      { eyebrow: "Lines of Coverage", title: "Coverage built for youth and amateur sports.", body: "From your first team to a multi-program organization, we place the lines that keep you protected on and off the field.", listRows: [
        ["General Liability","General liability coverage tailored for youth sports. Ensure your team, coaches, and facilities are covered."],
        ["Directors & Officers Coverage","Assurance that the game can still go on."],
        ["Hired/Non-Owned Auto","Reduce your team's risk with HNOA insurance and ensure your employees are covered when it comes to team travel."],
        ["Employee Benefits","Take care of your employees the way they take care of your athletes, with our full suite of employee benefits."],
        ["Participant Accident Medical","Unfortunately, accidents do happen. When they do, make sure your participants and volunteers receive proper medical treatment."],
      ] },
    ]} />,
  "/fitness": <SimplePage eyebrow="Fitness Insurance" title="Train hard. Insure smart." body="Insurance built for fitness businesses, visibility into your daily risk, and the tools to reduce it — fast, flexible coverage that grows with your franchise, gym, box, club, or studio."
    img={ATHLETE_IMG.fitnessImg} sections={[
      { eyebrow: "Built For", title: "Studios, gyms, and franchises.", cards: [["Group Fitness","Bootcamps, HIIT, and class-based studios."],["Strength & CrossFit","Box-level coverage with the right limits."],["Yoga & Pilates","Lower-impact pricing for the right risk."]] },
      { eyebrow: "Lines of Coverage", title: "Coverage that scales with your business.", body: "From your first studio to a multi-location franchise, we place the lines that keep operations running.", listRows: [
        ["General Liability","Protection from everyday risks like slips, trips, and lawsuits."],
        ["Umbrella Liability","Extra cushion for claims that exceed your basic policy limits."],
        ["Professional Liability","Defends instructors and therapists from claims tied to service."],
        ["Employment Practices Liability","Shields decision-makers from personal risk tied to business decisions."],
        ["Cyber","Safeguards member data and digital systems from breaches or attacks."],
        ["Directors & Officers (D&O)","Covers leadership from claims tied to business decisions."],
        ["Commercial Property","Covers buildings, equipment, and facility damage."],
        ["Commercial Auto","For company-owned vehicles used for pickups, deliveries, or transport."],
        ["Surety Bonding","Ensures compliance for permits, licenses, and vendor agreements."],
        ["Flood","Specific protection against flood-related loss not covered under standard policies."],
        ["Earthquake","Specialized coverage for seismic events."],
        ["Sexual Abuse Coverage","Protection against claims related to abuse or misconduct, helping safeguard members, staff, and the organization."],
        ["Workers Compensation","Coverage for employee injuries on the job, supporting medical care, lost wages, and employer protection."],
        ["Recovery and Wellness","Support for services like physical therapy, recovery programs, and wellness."],
      ] },
      { eyebrow: "In the news", title: "BFit builds a culture of proactive risk management.", spotlight: {
        summary: "See how BFit uses Players Health to get ahead of risk before it becomes a claim.",
        source: "Club Solutions Magazine",
        href: "https://clubsolutionsmagazine.com/2026/09/bfit-builds-a-culture-of-proactive-risk-management-with-players-health/",
        cta: "Read the article",
        testid: "bfit-spotlight",
      } },
    ]} />,
  "/collegiate": <SimplePage eyebrow="Collegiate" title="Protect your program in the NIL era." body="Insurance built for the NIL era, visibility into your program's exposure, and the tools to manage transfer, performance, and reputational risk — so athletic departments and agencies stay competitive."
    primaryHref="https://phnilcrm.com/insurance"
    img={ATHLETE_IMG.collegiate} sections={[{ eyebrow: "Solutions", title: "Coverage for the NIL era.", grid: "split-5", cards: [
      ["Critical Injury Insurance","Protects an athlete's NIL contract value if a covered injury ends or interrupts their season."],
      ["Performance Bonus & Incentive","Insures performance bonus pools against team milestone triggers like wins, championships, and tournament runs."],
      ["Athlete Transfer Insurance","Protects NIL investment when an athlete transfers to another program, built around contract value."],
      ["Disgrace Insurance","Coverage for reputational financial shock, protecting sponsorship and NIL value loss when athlete conduct creates sudden exposure."],
      ["Downside & Revenue Protection","A structured backstop against performance volatility, stabilizing budget planning for NIL and revenue share programs."],
    ] }]} />,
  "/schools": <SimplePage eyebrow="K-12 Schools" title="More than gear and game time." body="Insurance built for school athletics, visibility into district-wide risk, and the tools to protect student-athletes and staff — without adding to administrators' load."
    img={ATHLETE_IMG.k12} sections={[
      { eyebrow: "Built For", title: "Districts, schools, and conferences.", cards: [["District-Wide","One policy, every campus."],["Catastrophic","High-limit protection for serious injury."],["Concussion Care","Protocols and coverage aligned."]] },
      { eyebrow: "Lines of Coverage", title: "Coverage built for K-12 athletics.", grid: "split-4", cards: [
        ["Student Accident Insurance","Secondary insurance covering all full-time K-12 students during school-sanctioned times and events; becomes primary if the student has no other coverage."],
        ["Interscholastic Sports Coverage","Athletic injury protection for student athletes, including dedicated plans for football."],
        ["Catastrophic / Umbrella Coverage","District-level umbrella insurance for severe injury scenarios across school athletic programs."],
        ["Special Risk Insurance","Customized coverage for needs beyond standard student accident protection."],
      ] },
    ]} />,
  "/brokers": <SimplePage eyebrow="Brokers & Agents" title="A smarter way to place coverage." body="Speed, control, and confidence to quote, bind, and issue without delays — so you can win more business and serve sports clients better than the competition."
    img={ATHLETE_IMG.brokers} sections={[{ eyebrow: "Broker Tools", title: "Built for producers.", cards: [["Instant Quotes","Quote in minutes, not days."],["Digital Bind","E-sign and issue without paperwork."],["Commission Portal","Track and reconcile in one place."]] }]} />,
  "/about": <SimplePage eyebrow="About Players Health" title="We exist to make sports as safe as they are great." body="Players Health is the safety platform for youth, amateur, and small-business sports — covering the leagues, clubs, fitness studios, and schools that move kids and adults onto the field."
    img={ATHLETE_IMG.tennis}
    primaryCta="Work with us" primaryHref="/support"
    secondaryCta="See open roles" secondaryHref="/support"
    sections={[{ eyebrow: "Our Mission", title: "Safety as a standard, not an afterthought.", cards: [["Player-First","Every decision starts with the athlete."],["Tech + Touch","Modern tools, real humans on the phone."],["Carrier-Backed","Top-rated carriers, transparent pricing."]] }]} />,
  "/resources": <SimplePage eyebrow="Resources" title="Guides, playbooks, and reports." body="Practical content for the people running sports programs — from compliance checklists to coverage primers."
    img={ATHLETE_IMG.field} sections={[{ eyebrow: "Library", title: "Built by sports people, for sports people.", cards: [["The Coverage Primer","What every program actually needs."],["SafeSport Checklist","Quick-start compliance for clubs."],["Incident Playbook","From report to resolution."]] }]} />,
  "/support": <SimplePage eyebrow="Support" title="Talk to a sports specialist." body="Real people who know sports — quote questions, claims help, and policy support."
    img={ATHLETE_IMG.basketball} sections={[{ eyebrow: "Get In Touch", title: "We're here when you need us.", cards: [["Sales","new-business@playershealth.com"],["Claims","claims@playershealth.com"],["General","hello@playershealth.com"]] }]} />,
};

const QuotePage = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", sport: "Youth Sports", participants: "", notes: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/quote`, form);
      setStatus("sent");
      setForm({ name: "", email: "", organization: "", sport: "Youth Sports", participants: "", notes: "" });
    } catch { setStatus("error"); }
  };
  return (
    <>
      <Hero eyebrow="Get a Quote" title="Quotes in minutes. Coverage that fits." body="Tell us about your program. We'll come back with a tailored quote — usually same day."
        img={ATHLETE_IMG.basketball} primaryCta="Talk to us instead" primaryHref="/support" secondaryCta="Back to home" secondaryHref="/" />
      <Section alt>
        <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", border: "1px solid var(--ink-200)", borderRadius: 16, padding: 40 }}>
          <h2 className="display display-s" style={{ marginBottom: 24 }}>Your details</h2>
          {status === "sent" ? (
            <div data-testid="quote-success" style={{ padding: 24, background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 12, color: "#065F46" }}>
              <strong>Got it.</strong> A specialist will reach out within one business day.
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form">
              <div className="split-2">
                <div className="form-row"><label>Full name</label><input required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="quote-name" /></div>
                <div className="form-row"><label>Work email</label><input required type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="quote-email" /></div>
              </div>
              <div className="form-row"><label>Organization</label><input required value={form.organization} onChange={e=>setForm({...form, organization: e.target.value})} data-testid="quote-org" /></div>
              <div className="split-2">
                <div className="form-row"><label>Type of program</label>
                  <select value={form.sport} onChange={e=>setForm({...form, sport: e.target.value})} data-testid="quote-sport">
                    {["Youth Sports","Fitness / Gym","Collegiate","K-12 School","Broker / Agent","Other"].map(o=> <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-row"><label>Approx. participants</label><input value={form.participants} onChange={e=>setForm({...form, participants: e.target.value})} placeholder="e.g. 250" data-testid="quote-participants" /></div>
              </div>
              <div className="form-row"><label>Anything else?</label><textarea value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} data-testid="quote-notes" /></div>
              <button type="submit" disabled={status==="sending"} className="btn btn-accent" style={{ width: "100%", marginTop: 8 }} data-testid="quote-submit">
                {status === "sending" ? "Sending…" : "Request my quote"}
              </button>
              {status === "error" && <p style={{ color: "#B91C1C", marginTop: 12 }}>Something went wrong. Try again or email hello@playershealth.com.</p>}
            </form>
          )}
        </div>
      </Section>
    </>
  );
};

function App() {
  const [route, setRoute] = useState(getPath());
  const lenisRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reduce]);

  useEffect(() => {
    const onPop = () => {
      setRoute(getPath());
      if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", onPop);
    // Global click interceptor — pushState-based navigation for internal links.
    const onClick = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const a = e.target.closest && e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:") || a.target === "_blank" || a.hasAttribute("download")) return;
      // Legacy hash-anchor scroll (e.g. "#appointment") — let browser handle
      if (href.startsWith("#") && !href.startsWith("#/")) return;
      // Legacy /#/foo — normalize to /foo
      const normalized = href.startsWith("#/") ? href.slice(1) : href;
      if (!normalized.startsWith("/")) return;
      e.preventDefault();
      if (normalized !== window.location.pathname + window.location.search) {
        window.history.pushState({}, "", normalized);
        setRoute(getPath());
        if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClick);
    };
  }, []);

  // GA4 page_view on every route change (HashRouter — auto page_view is disabled in the tag).
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: route,
    });
  }, [route]);

  // Per-route SEO — title, description, canonical, breadcrumb JSON-LD.
  useEffect(() => {
    const robots = document.head.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", route.startsWith("/admin") ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    if (route.startsWith("/admin")) { document.title = "Admin | Players Health"; return; }
    const seo = SEO_META[route] || SEO_META["/"];
    document.title = seo.title;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/[\[\]"]/g, "").split("=");
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:url"]', "content", `https://playershealth.com${route}`);
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", route === "/" ? "https://playershealth.com/" : `https://playershealth.com${route}`);

    // BreadcrumbList JSON-LD (skip on home)
    const existing = document.getElementById("ph-breadcrumb-jsonld");
    if (existing) existing.remove();
    if (route !== "/" && seo.crumb) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ph-breadcrumb-jsonld";
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://playershealth.com/" },
          { "@type": "ListItem", position: 2, name: seo.crumb, item: `https://playershealth.com${route}` },
        ],
      });
      document.head.appendChild(script);
    }
  }, [route]);

  if (route === "/admin/guidebook-leads") return <AdminLeadsPage />;
  const CUSTOM = { "/resources": <ResourcesPage />, "/guidebooks": <GuidebooksPage />, "/about": <AboutPage />, "/support": <ContactPage />, "/brokers": <BrokerPage />, "/athlete-safety": <AthleteSafetyPage />, "/privacy": <LegalPage doc={PRIVACY_POLICY} />, "/terms": <LegalPage doc={TERMS_CONDITIONS} /> };
  const articleSlug = route.startsWith("/resources/") ? route.slice("/resources/".length) : null;
  const page = articleSlug
    ? (ARTICLE_BY_SLUG[articleSlug] ? <ArticlePage slug={articleSlug} /> : <ArticlePage slug={articleSlug} />)
    : route === "/"
      ? <HomePage />
      : route === "/quote"
        ? <QuotePage />
        : (CUSTOM[route] || PAGES[route] || <HomePage />);
  return (
    <div className="ph-app" data-testid="ph-app">
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />
      <Nav route={route} />
      <main key={route} className="page-fade" data-testid="ph-page">{page}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

export default App;
